import type { AlertService } from "@ui";
import type { Corpus } from "@pienso/ingest-types/corpus.interface";
import { TRANSFORM_TYPE, type Transform, type TransformWithCorpus } from "@pienso/ingest-types/transform.interface";
import type { GridDataProvider } from "../data-grid/grid-data.provider";
import type { GridRendererProvider } from "../data-grid/grid-renderer-provider.interface";
import type { CorpusDataService, TransformDetails } from "../services/corpus-data.service";
import {
  type AbstractTransformerService,
  TRANSFORMER_ACTION,
  type TRANSFORMER_ACTIONS,
  TRANSFORMER_STATE,
} from "./abstract-transformer.service";
import type { ToolbarItem } from "./toolbar-item";
import type { Transformer } from "./transformer.interface";
import type { TransformerGridDataService } from "./transformer-grid-data.service";

export abstract class AbstractTransformer implements Transformer {
  protected corpus: Corpus | undefined;
  protected _transform: Transform | undefined;
  private promiseResolve: ((xform: TransformWithCorpus | PromiseLike<TransformWithCorpus>) => void) | undefined;
  private promiseReject: ((reason: any) => void) | undefined;

  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly transformTypes: TRANSFORM_TYPE[],
    protected corpusService: CorpusDataService,
    protected alertService: AlertService,
    public service: AbstractTransformerService,
    protected dataService: TransformerGridDataService,
    protected _toolbar?: ToolbarItem,
    protected _dataProvider?: GridDataProvider,
    protected _rendererProvider?: GridRendererProvider,
  ) {
    this._toolbar = _toolbar || undefined;
    this._dataProvider = _dataProvider || undefined;
    service.action$.subscribe((action: TRANSFORMER_ACTIONS) => {
      switch (action) {
        case TRANSFORMER_ACTION.CANCEL:
          this.cancel();
          break;
        case TRANSFORMER_ACTION.SAVE:
          this.commit(this.createSavePayload());
          break;
      }
    });
  }

  public get transform() {
    return this._transform;
  }

  public abstract describe(xform: Transform): string;

  public get toolbar() {
    return this._toolbar;
  }
  public get gridDataProvider() {
    return this._dataProvider;
  }
  public get gridRendererProvider() {
    return this._rendererProvider;
  }

  async init(corpus: Corpus): Promise<TransformWithCorpus> {
    this.dataService.ignoreLoads = true;
    try {
      this.reset();
      this.corpus = corpus;
      if (this.toolbar) {
        this.toolbar.corpus = corpus;
      }
      this.dataService.ignoreLoads = false;
      // this.dataService.reload();
      return new Promise<TransformWithCorpus>((resolve, reject) => {
        this.promiseResolve = resolve;
        this.promiseReject = reject;
      });
    } catch (e) {
      this.dataService.ignoreLoads = false;
      throw e;
    }
  }

  edit(transform: Transform): Promise<TransformWithCorpus> {
    this.dataService.ignoreLoads = true;
    try {
      this.corpus = transform.corpus;
      this._transform = transform;
      if (this.toolbar && this.corpus) {
        this.toolbar.corpus = this.corpus;
      }
      this.initFromTransform(transform);
      this.service.state = TRANSFORMER_STATE.DONE;
      this.dataService.ignoreLoads = false;
      // this.dataService.reload();
      return new Promise<TransformWithCorpus>((resolve, reject) => {
        this.promiseResolve = resolve;
        this.promiseReject = reject;
      });
    } catch (e) {
      this.dataService.ignoreLoads = false;
      throw e;
    }
  }

  protected commit(details: TransformDetails) {
    if (!this.corpus) {
      throw new Error("Corpus is not set. Cannot commit transform.");
    }
    this.service.state = TRANSFORMER_STATE.PENDING;
    const obs = this.transform
      ? this.corpusService.updateTransform(this.corpus.id, this.transform.id, details)
      : this.corpusService.transformCorpus(this.corpus.id, details);
    obs.subscribe(
      (transform) => {
        this.completed(transform);
        this.promiseResolve!(transform);
      },
      (error) => {
        this.service.state = TRANSFORMER_STATE.ERROR;
        if (error) {
          this.alertService.error(error);
        }
      },
    );
  }

  protected cancel(reason?: any) {
    this.promiseReject!(reason);
  }

  reset() {
    this.corpus = undefined;
    this._transform = undefined;
    this.service.reset();
  }

  close() {}

  protected completed(transform: TransformWithCorpus) {
    this._transform = transform;
    this.corpus = transform.corpus;
    this._toolbar!.corpus = transform.corpus;
    this.service.state = TRANSFORMER_STATE.DONE;
  }

  protected abstract createSavePayload(): TransformDetails;

  protected abstract initFromTransform(xform: Transform): void;
}
